// vista-web entry — tiny hash router, no framework shell.
// Routes: #/login, #/auth?token=… (from the email), #/ (team | contributor
// cabinet by session role), #/shoot (the capture screen), #/contributor/:id
// (card), #/manage (manager only).
import { render } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import {
  api,
  clearToken,
  getActiveRole,
  getContributorToken,
  getPrimaryToken,
  setActiveRole,
  setContributorToken,
  type VistaContributorMe,
  type VistaMeRes as Me,
  type VistaStaffMe,
} from './api';
import { asLang, getStoredLang, makeT, setStoredLang, type Lang, type T } from './i18n';
import { LoginPage } from './pages/LoginPage';
import { PoolPage } from './pages/PoolPage';
import { ContributorPage } from './pages/ContributorPage';
import { ManagePage } from './pages/ManagePage';
import { MySpacePage } from './pages/MySpacePage';
// Экран съёмки тянет за собой движок записи (Mediabunny + WebCodecs-обвязка) —
// это больше половины бандла. Грузим его отдельным чанком: кабинеты, дашборды и
// экран входа не должны платить за код, который нужен одному экрану из семи.
//
// ЛОВУШКА, на которой уже горел internal: вкладка, открытая ДО деплоя, держит
// старый index.js и просит чанк со старым хэшем — а его больше нет. Pages
// отвечает не 404, а 200 с index.html, импорт падает «Failed to fetch
// dynamically imported module», и человек видит пустой экран вместо записи.
// Поэтому провал загрузки чанка = перезагрузка страницы: пользователь на
// съёмке не должен разбираться, почему пусто.
const CapturePage = lazy(() =>
  import('./pages/CapturePage')
    .then((m) => ({ default: m.CapturePage }))
    .catch((e) => {
      // Один раз за визит: reload-петля хуже пустого экрана.
      if (!sessionStorage.getItem('vista.chunk.reloaded')) {
        sessionStorage.setItem('vista.chunk.reloaded', '1');
        location.reload();
      }
      throw e;
    }),
);
import { QueuePage } from './pages/QueuePage';
import { UploadPage } from './pages/UploadPage';
import { FinancePage } from './pages/FinancePage';
import { InstallPrompt, UpdateBanner } from './components/InstallPrompt';
import { ProfileMenu, type SwitchItem, type LangItem } from './components/ProfileMenu';
import { PasswordDialog } from './components/PasswordDialog';
import { captureInstallPrompt, registerSW, startVersionWatch } from './pwa';
import './styles.css';

// До первого рендера: beforeinstallprompt стреляет один раз и рано — если не
// поймать его сейчас, кнопка установки не появится вовсе.
captureInstallPrompt();
registerSW();
// Досылка рапорта об аборте, не дошедшего до сервера в прошлой сессии.
void import('./capture/vistaR2Stream').then((m) => m.flushAbortDeadLetter());
// Авто-обновление: тихий reload, кроме моментов, когда перезагрузка стоит
// материала — на съёмке и на заливке файла (там висит баннер вместо reload).
startVersionWatch(() => !location.hash.startsWith('#/shoot') && !location.hash.startsWith('#/upload'));

// Значок профиля — человечек в круге (как ProfileIcon в apps/kids-web/src/Shell.tsx):
// одна и та же кнопка вместо текстового имени в топбаре, у контрибьютора и у
// штаба (Tim: «вместо текстовой кнопки — значок профиля как в кидс»).
// Нижний таб-бар — паттерн kids: на телефоне навигация живёт под большим
// пальцем, а не в шапке (скроллящийся seg в топбаре «ездил» и распирал экран).
// Иконки — инлайн-SVG в духе kids Shell.tsx: без иконочного шрифта в бандле.
const TAB_ICONS: Record<string, preact.ComponentChildren> = {
  shoot: <><rect x="2.5" y="6.5" width="13" height="11" rx="2" /><path d="M15.5 10.5 21 7.5v9l-5.5-3" /></>,
  upload: <><path d="M12 16V4.5" /><path d="M7 9.5 12 4.5l5 5" /><path d="M4.5 19.5h15" /></>,
  mine: <><path d="M8 6h12M8 12h12M8 18h12" /><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></>,
  team: <><circle cx="9" cy="8" r="3.5" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 5.5a3.5 3.5 0 0 1 0 5M17.5 20a6 6 0 0 0-2-4.5" /></>,
  queue: <><rect x="3.5" y="3.5" width="7" height="7" rx="1" /><rect x="13.5" y="3.5" width="7" height="7" rx="1" /><rect x="13.5" y="13.5" width="7" height="7" rx="1" /><rect x="3.5" y="13.5" width="7" height="7" rx="1" /></>,
  finance: <><rect x="2.5" y="5.5" width="19" height="13" rx="2" /><path d="M2.5 10h19" /></>,
  manage: <><circle cx="12" cy="12" r="3" /><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" /></>,
};

function TabLink({ href, icon, label, on }: { href: string; icon: string; label: string; on: boolean }) {
  return (
    <a href={href} class={`tabbar-item ${on ? 'on' : ''}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        {TAB_ICONS[icon]}
      </svg>
      <span>{label}</span>
    </a>
  );
}

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
      stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.7" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function useHash(): string {
  const [hash, setHash] = useState(location.hash || '#/');
  useEffect(() => {
    const on = () => setHash(location.hash || '#/');
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);
  return hash;
}

// F3 — the default cabinet on login/reload is the highest-PRIORITY role, not
// necessarily the highest RANK: 'contributor' deliberately sits below every
// staff rank (including viewer) because it's a cabinet you switch INTO on
// purpose, never the one a multi-role person lands on by accident.
const ROLE_PRIORITY: Record<string, number> = { manager: 3, admin: 2, viewer: 1, contributor: 0 };
const highestRole = (roles: string[]): string =>
  roles.reduce((best, r) => ((ROLE_PRIORITY[r] ?? -1) > (ROLE_PRIORITY[best] ?? -1) ? r : best), roles[0]);

// Ярлык роли и подпись переключателя кабинетов — на языке смотрящего. Раньше
// они были русскими литералами «потому что это штабная штука»; с появлением
// англоязычного админа это перестало быть правдой.
const roleSwitchLabel = (t: T, role: string): string =>
  t('staff.cabinet', { role: t(`staff.role.${role}`) });

const CONTRIBUTOR_LANG_ITEMS: LangItem[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
];

/**
 * F3 — exchange the staff session for a contributor one and fetch that
 * cabinet's own identity. Caller MUST have already persisted
 * activeRole='contributor' (setActiveRole) before calling this: api.me()
 * below resolves its Authorization header through getToken(), which reads
 * that flag to decide which stored token to send.
 */
async function enterContributorCabinet(): Promise<VistaContributorMe> {
  let token = getContributorToken();
  if (!token) {
    const r = await api.asContributor();
    token = r.token;
    setContributorToken(token);
  }
  const cme = await api.me();
  if (cme.role !== 'contributor') {
    // Unreachable in practice (as-contributor only ever mints a contributor
    // JWT), but a defensive throw here beats silently rendering the wrong
    // cabinet with the wrong person's data.
    throw new Error('as-contributor session did not resolve to a contributor');
  }
  return cme;
}

function App() {
  const hash = useHash();
  // The base identity, ALWAYS fetched with the primary/staff token (see
  // api.mePrimary) — this is what carries the full `roles` set the switcher
  // is built from. For a plain contributor account this IS their only me.
  const [primaryMe, setPrimaryMe] = useState<Me | null | 'loading'>('loading');
  // Lazily populated only when a STAFF person switches into the contributor
  // cabinet (F3); stays null for plain contributor accounts and for staff who
  // never switch.
  const [contributorMe, setContributorMe] = useState<VistaContributorMe | null>(null);
  const [activeRole, setActiveRoleState] = useState<string | null>(null);
  // Штабу язык профиля не хранится нигде: решает выбор в меню, а до него —
  // язык браузера. Всё незнакомое приложению падает в английский (asLang).
  const [lang, setLang] = useState<Lang>(() =>
    asLang(getStoredLang() ?? navigator.language.slice(0, 2)),
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    if (!getPrimaryToken()) {
      setPrimaryMe(null);
      return;
    }
    api
      .mePrimary()
      .then(async (u) => {
        setPrimaryMe(u);
        const roles = u.role === 'contributor' ? ['contributor'] : u.roles;
        const stored = getActiveRole();
        const initial = stored && roles.includes(stored) ? stored : highestRole(roles);
        setActiveRole(initial);
        setActiveRoleState(initial);

        if (u.role === 'contributor') {
          setLang(asLang(getStoredLang() ?? u.uiLang));
          return;
        }
        if (initial === 'contributor') {
          // A reload landed us mid-switch (activeRole persisted from a
          // previous visit) — re-establish the contributor identity before
          // rendering, same path the profile-menu click takes below.
          try {
            const cme = await enterContributorCabinet();
            setContributorMe(cme);
            setLang(asLang(getStoredLang() ?? cme.uiLang));
          } catch {
            const fallback = highestRole(roles.filter((r) => r !== 'contributor'));
            setActiveRole(fallback);
            setActiveRoleState(fallback);
          }
        }
      })
      .catch(() => setPrimaryMe(null));
  }, []);

  // Email link lands here regardless of session state.
  if (hash.startsWith('#/auth')) {
    const token = new URLSearchParams(hash.split('?')[1] ?? '').get('token') ?? undefined;
    return <LoginPage authToken={token} />;
  }

  const contributorId = hash.startsWith('#/contributor/')
    ? decodeURIComponent(hash.slice('#/contributor/'.length))
    : null;

  if (primaryMe === 'loading') return <div class="empty">{makeT(lang)('staff.loading')}</div>;
  if (primaryMe === null || hash.startsWith('#/login')) return <LoginPage />;

  const doLogout = () => {
    clearToken();
    location.hash = '#/login';
    location.reload();
  };

  const switchRole = async (role: string) => {
    if (role === activeRole) return;
    if (role !== 'contributor') {
      setActiveRole(role);
      setActiveRoleState(role);
      return;
    }
    // Persist BEFORE fetching: enterContributorCabinet's api.me() call reads
    // this flag through getToken() to pick the right stored token.
    setActiveRole('contributor');
    setActiveRoleState('contributor');
    if (primaryMe.role !== 'contributor' && !contributorMe) {
      try {
        const cme = await enterContributorCabinet();
        setContributorMe(cme);
        setLang(asLang(getStoredLang() ?? cme.uiLang));
      } catch {
        // primaryMe.role !== 'contributor' is already guaranteed by the `if`
        // above (that's what made this branch reachable), so `.roles` here
        // is always the staff role set — no ternary needed.
        const fallback = highestRole(primaryMe.roles.filter((r) => r !== 'contributor'));
        setActiveRole(fallback);
        setActiveRoleState(fallback);
      }
    }
  };

  // Contributor cabinet: either a genuine contributor session (primaryMe
  // itself) or a staff person's switched-in one (contributorMe, fetched
  // above). isContributorCabinet tracks the SCREEN, not the login identity —
  // a multi-role staff member has one and can be on either.
  const isContributorCabinet = activeRole === 'contributor';

  if (isContributorCabinet) {
    const t = makeT(lang);
    const meC: VistaContributorMe | null = primaryMe.role === 'contributor' ? primaryMe : contributorMe;
    if (!meC) {
      // Still minting the as-contributor token right after a switch.
      return <div class="empty">{t('staff.loading')}</div>;
    }
    const shooting = hash.startsWith('#/shoot');
    const uploading = hash.startsWith('#/upload');
    const roles = primaryMe.role === 'contributor' ? [] : primaryMe.roles;
    const switchItems: SwitchItem[] = roles
      .filter((r) => r !== activeRole)
      .map((r) => ({ role: r, label: roleSwitchLabel(t, r) }));

    return (
      <div class="shell">
        <div class="topbar">
          <div class="brand">
            <img src="/logo/weern-mark-green.svg" alt="" class="brand-mark" />
            <span>Vista</span>
          </div>
          <button
            type="button"
            class="profile-btn"
            aria-label={t('nav.profile')}
            onClick={() => setProfileOpen(true)}
          >
            <ProfileIcon />
          </button>
        </div>
        <UpdateBanner t={t} />
        {!shooting && <InstallPrompt t={t} />}
        {shooting ? (
          <Suspense fallback={<div class="empty">{t('loading')}</div>}>
            <CapturePage t={t} lang={lang} />
          </Suspense>
        ) : uploading ? (
          <UploadPage t={t} lang={lang} />
        ) : (
          <MySpacePage t={t} lang={lang} />
        )}

        <nav class="tabbar">
          <TabLink href="#/shoot" icon="shoot" label={t('nav.shoot')} on={shooting} />
          {/* Запасной путь стоит рядом с основным, а не спрятан: человек, у
              которого рекордер только что не сработал, не должен искать, куда
              деть уже снятое видео. */}
          <TabLink href="#/upload" icon="upload" label={t('nav.upload')} on={uploading} />
          <TabLink href="#/" icon="mine" label={t('nav.mine')} on={!shooting && !uploading} />
        </nav>

        {profileOpen && (
          <ProfileMenu
            name={meC.name}
            email={meC.email}
            switchItems={switchItems}
            onSwitchRole={switchRole}
            langItems={CONTRIBUTOR_LANG_ITEMS}
            activeLang={lang}
            onSetLang={(l) => {
              setStoredLang(l);
              setLang(l);
            }}
            langLabel={t('profile.lang')}
            buildLabel={t('staff.build')}
            passwordLabel={t('profile.password')}
            onChangePassword={() => setPwdOpen(true)}
            logoutLabel={t('nav.logout')}
            onLogout={doLogout}
            onClose={() => setProfileOpen(false)}
          />
        )}
        {pwdOpen && (
          <PasswordDialog
            title={t('profile.password')}
            labelNew={t('profile.password.new')}
            labelConfirm={t('profile.password.confirm')}
            minLenMsg={t('profile.password.tooShort')}
            mismatchMsg={t('profile.password.mismatch')}
            cancelLabel={t('profile.password.cancel')}
            submitLabel={t('profile.password.save')}
            savingLabel={t('profile.password.saving')}
            doneMsg={t('profile.password.done')}
            onSubmit={(password) => api.changePassword(password).then(() => undefined)}
            onClose={() => setPwdOpen(false)}
          />
        )}
      </div>
    );
  }

  // Staff cabinet. Reachable only when primaryMe.role !== 'contributor' — a
  // plain contributor account has roles=['contributor'] and activeRole is
  // therefore always 'contributor', which returns above.
  const staffMe = primaryMe as VistaStaffMe;
  const t = makeT(lang);

  // The worker gates every /manage endpoint too — this only decides what to
  // render, not who may call what.
  const isManage = hash.startsWith('#/manage') && staffMe.role === 'manager';
  const isQueue = hash.startsWith('#/queue');
  const isFinance = hash.startsWith('#/finance');
  const staffSwitchItems: SwitchItem[] = staffMe.roles
    .filter((r) => r !== activeRole)
    .map((r) => ({ role: r, label: roleSwitchLabel(t, r) }));

  return (
    <div class="shell">
      <div class="topbar">
        <div class="brand">
          <img src="/logo/weern-mark-green.svg" alt="" class="brand-mark" />
          <span>Vista</span>
          <span class="brand-sub">{t('staff.sub')}</span>
        </div>
        <button
          type="button"
          class="profile-btn"
          aria-label={`${staffMe.name} · ${t(`staff.role.${staffMe.role}`)}`}
          onClick={() => setProfileOpen(true)}
        >
          <ProfileIcon />
        </button>
      </div>
      <UpdateBanner t={t} />
      {contributorId ? (
        <ContributorPage me={staffMe} id={contributorId} t={t} />
      ) : isManage ? (
        <ManagePage me={staffMe} t={t} />
      ) : isQueue ? (
        <QueuePage me={staffMe} t={t} />
      ) : isFinance ? (
        <FinancePage me={staffMe} t={t} />
      ) : (
        <PoolPage me={staffMe} t={t} />
      )}

      <nav class="tabbar">
        <TabLink
          href="#/"
          icon="team"
          label={t('staff.tab.team')}
          on={!isManage && !isQueue && !isFinance && !contributorId}
        />
        <TabLink href="#/queue" icon="queue" label={t('staff.tab.queue')} on={isQueue} />
        <TabLink href="#/finance" icon="finance" label={t('staff.tab.money')} on={isFinance} />
        {staffMe.role === 'manager' && (
          <TabLink href="#/manage" icon="manage" label={t('staff.tab.manage')} on={isManage} />
        )}
      </nav>

      {profileOpen && (
        <ProfileMenu
          name={staffMe.name}
          email={staffMe.email}
          switchItems={staffSwitchItems}
          onSwitchRole={switchRole}
          langItems={CONTRIBUTOR_LANG_ITEMS}
          activeLang={lang}
          onSetLang={(l) => {
            setStoredLang(l);
            setLang(l);
          }}
          langLabel={t('staff.lang')}
          buildLabel={t('staff.build')}
          passwordLabel={t('staff.password')}
          onChangePassword={() => setPwdOpen(true)}
          logoutLabel={t('staff.logout')}
          onLogout={doLogout}
          onClose={() => setProfileOpen(false)}
        />
      )}
      {pwdOpen && (
        <PasswordDialog
          title={t('staff.password')}
          labelNew={t('staff.password.new')}
          labelConfirm={t('staff.password.confirm')}
          minLenMsg={t('staff.password.tooShort')}
          mismatchMsg={t('staff.password.mismatch')}
          cancelLabel={t('staff.password.cancel')}
          submitLabel={t('staff.password.save')}
          savingLabel={t('staff.password.saving')}
          doneMsg={t('staff.password.done')}
          onSubmit={(password) => api.changePassword(password).then(() => undefined)}
          onClose={() => setPwdOpen(false)}
        />
      )}
    </div>
  );
}

render(<App />, document.getElementById('app')!);
